import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Box, Fade, Flex, Text } from '@chakra-ui/react';

import { BillingFrequency } from '~/features/checkout/models/checkout';
import { COUPON_COOKIE } from '~/features/cookies/constants';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { setClientCookie } from '~/features/cookies/utils/set-client-cookie';
import { getCouponCodeFromCookie } from '~/features/discount/utils/get-coupon-code-from-cookie';
import { isPersonalizedDiscountCoupon } from '~/features/discount/utils/is-pd-coupon';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { PriceCardSticker } from '~/features/promotions/components/price-card-sticker';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';
import { CountdownConfig } from '~/features/promotions/models/countdown-config';
import { hasDifferentAppliedCoupon } from '~/features/promotions/utils/has-different-applied-coupon';
import { useClientRender } from '~/hooks/use-client-render';
import { parseMs, useCountdown } from '~/hooks/use-countdown';

import { CountdownTimer } from './countdown-timer';

type ButtonRenderProp = (props?: {
  isCountdownActive?: boolean;
  isFallback?: boolean;
  couponPercentOff?: number | null;
  countdownConfig?: CountdownConfig | null;
  couponCode?: string | null;
  forcedButtonLabel?: string | null;
  until?: string;
  onButtonClick?: () => Promise<void>;
}) => ReactNode;

const defaultButtonLabel = 'Enroll Now';

// A stacked version of the countdown timer. Looks like this:
//
// |--------------------------------|
// |Custom countdown blurb | Timer  |
// |--------------------------------|
// |       [Enroll Now btn]         |
// |--------------------------------|
export function CountdownCompactContainer({
  renderButton,
  selectedPlan,
  showSticker = true,
}: {
  renderButton: (selectedPlan: BillingFrequency, buttonLabel: string) => ReactNode;
  selectedPlan: BillingFrequency;
  showSticker?: boolean;
}) {
  const [isTabVisible, setIsTabVisible] = useState(true);
  const { data: promoData, status: promoStatus } = usePromotion();
  const clientReady = useClientRender();

  const countdownConfig = promoData?.countdownConfig;

  // Note: this effect is necessary to refresh the countdown when the browser tab is stale and is revisited.
  useEffect(() => {
    function onVisibilityChange() {
      setIsTabVisible(document.visibilityState === 'visible');
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const buttonLabel = useMemo(() => {
    if (promoStatus !== 'success') {
      return defaultButtonLabel;
    }

    return countdownConfig?.buttonLabelOverride ?? defaultButtonLabel;
  }, [countdownConfig?.buttonLabelOverride, promoStatus]);

  const hasActiveSubscription = clientReady ? getActiveSubscriptionCookie() : null;

  const fallbackButton = (
    <Box flex={{ base: 1, md: 'unset' }}>
      {renderButton(selectedPlan, buttonLabel)}
      {showSticker && <PriceCardSticker />}
    </Box>
  );

  if (hasActiveSubscription || !countdownConfig) {
    return <>{fallbackButton}</>;
  }

  switch (countdownConfig.countdownTo) {
    case 'promoEndDate': {
      return (
        <CountdownToPromoEndDateLayout
          key={isTabVisible ? 1 : 0}
          countdownConfig={countdownConfig}
          fallbackSlot={fallbackButton}
          renderButton={() => renderButton(selectedPlan, buttonLabel)}
        />
      );
    }
    case 'couponExpiry': {
      return (
        <CountdownToCouponExpiryLayout
          key={isTabVisible ? 1 : 0}
          countdownConfig={countdownConfig}
          fallbackSlot={fallbackButton}
          renderButton={() => renderButton(selectedPlan, buttonLabel)}
        />
      );
    }
    default:
      return <>{fallbackButton}</>;
  }
}

function CountdownLayout({
  renderButton,
  until,
  couponPercentOff,
  countdownConfig,
  couponCode,
  forcedButtonLabel,
  onButtonClick,
}: {
  renderButton: ButtonRenderProp;
  until: string;
  couponPercentOff?: number | null;
  countdownConfig: CountdownConfig;
  couponCode?: string | null;
  forcedButtonLabel?: string | null;
  onButtonClick?: () => Promise<void>;
}) {
  const date = new Date(until);
  const { ms, isActive } = useCountdown(date);
  const [days, hours, minutes, seconds] = parseMs(ms);

  const blurb = countdownConfig?.blurb || 'Limited Time Offer!';
  const totalHours = days * 24 + hours;
  const hoursOnly = countdownConfig.displayMode === 'hms' && totalHours <= 100;
  const displayHours = hoursOnly ? totalHours : hours;

  return (
    <Flex
      alignItems='center'
      backgroundColor={isActive ? 'supportBlue.100' : 'unset'}
      borderRadius='md'
      color='white'
      direction='column'
      gap={2}
      overflow='hidden'
      p={2}
      transition='background-color 300ms ease-in-out'
    >
      <Flex
        alignItems='center'
        as={Fade}
        flex='1'
        in={isActive}
        justifyContent='flex-end'
        width='100%'
        px={{
          base: 0,
          sm: 0,
        }}
        unmountOnExit
      >
        <Flex alignItems='center' gap={2} justifyContent='space-between' width='100%'>
          <Flex alignItems='center'>
            <Text fontSize='md' fontWeight='600' noOfLines={2}>
              {blurb}
            </Text>
          </Flex>
          <CountdownTimer
            days={days}
            hours={displayHours}
            hoursOnly={hoursOnly}
            minutes={minutes}
            seconds={seconds}
            containerProps={{
              flex: '0 0 50%',
            }}
          />
        </Flex>
      </Flex>
      <Box flex={!isActive ? { base: 1, md: 'unset' } : undefined} width='100%'>
        {renderButton({
          isCountdownActive: isActive,
          couponPercentOff,
          countdownConfig,
          couponCode,
          forcedButtonLabel,
          until,
          onButtonClick,
        })}
      </Box>
    </Flex>
  );
}

export function CountdownToCouponExpiryLayout({
  renderButton,
  countdownConfig,
  fallbackSlot,
}: {
  renderButton: ButtonRenderProp;
  countdownConfig: CountdownConfig;
  fallbackSlot: ReactNode;
}) {
  const { data: promotion } = usePromotion();
  const { currency } = useCurrency();
  const { data: pricePlans, status: pricePlanStatus } = usePricePlans(currency);
  const clientReady = useClientRender();

  const individualMonthlyPrice = pricePlans?.prices?.monthly?.individual;
  const couponApplied = individualMonthlyPrice?.couponApplied;
  const couponExpiresAt = individualMonthlyPrice?.couponExpiresAt;
  const couponPercentOff = individualMonthlyPrice?.couponPercentOff;
  const couponCode = clientReady ? getCouponCodeFromCookie() : null;

  if (pricePlanStatus !== 'success' || !couponApplied || !couponExpiresAt || !couponPercentOff) {
    return <>{fallbackSlot}</>;
  }

  if (promotion?.type === 'personalized-discount' && couponCode && !isPersonalizedDiscountCoupon(couponCode)) {
    return <>{fallbackSlot}</>;
  }

  if (!isTimerShown(couponExpiresAt, countdownConfig.showTimerFromHours)) {
    return <>{fallbackSlot}</>;
  }

  return (
    <CountdownLayout
      countdownConfig={countdownConfig}
      couponPercentOff={couponPercentOff}
      forcedButtonLabel={`Take ${couponPercentOff}% Off`}
      renderButton={renderButton}
      until={couponExpiresAt}
    />
  );
}

export function CountdownToPromoEndDateLayout({
  renderButton,
  countdownConfig,
  fallbackSlot,
}: {
  renderButton: ButtonRenderProp;
  countdownConfig: CountdownConfig;
  fallbackSlot: ReactNode;
}) {
  const { data: promoData } = usePromotion();
  const { currency } = useCurrency();
  const pricePlans = usePricePlans(currency);
  const clientReady = useClientRender();
  const appliedCouponCode = clientReady ? getCouponCodeFromCookie() : null;
  const endDateTime = promoData?.endDateTime;
  const promoCode = promoData?.promoCode;

  async function buttonClickHandler() {
    if (promoCode && endDateTime) {
      setClientCookie(COUPON_COOKIE, promoCode.trim(), new Date(endDateTime));
      await pricePlans.refetch();
    }
  }

  if (!promoCode || !endDateTime) {
    return <>{fallbackSlot}</>;
  }

  if (
    hasDifferentAppliedCoupon({
      appliedCouponCode,
      couponCodeApplied: Boolean(appliedCouponCode),
      promotionCouponCode: promoCode,
    })
  ) {
    return <>{fallbackSlot}</>;
  }

  if (!isTimerShown(endDateTime, countdownConfig.showTimerFromHours)) {
    return <>{fallbackSlot}</>;
  }

  return (
    <CountdownLayout
      countdownConfig={countdownConfig}
      couponCode={promoData.promoCode}
      forcedButtonLabel={countdownConfig.buttonLabelOverride}
      renderButton={renderButton}
      until={promoData.endDateTime}
      onButtonClick={buttonClickHandler}
    />
  );
}

export function isTimerShown(promoEndTimeStr: string, showTimerFromHours?: number | null): boolean {
  const now = new Date();
  const promoEndTime = new Date(promoEndTimeStr);
  const thresholdMs = (showTimerFromHours ?? 48) * 60 * 60 * 1000;
  const showTimerFrom = new Date(promoEndTime.getTime() - thresholdMs);
  return now < promoEndTime && now > showTimerFrom;
}
